import { useState, useEffect } from "react";
import { X, Cookie, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { setConsent, ConsentStatus } from "@/lib/consent";

interface CookieSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentStatus: ConsentStatus;
}

const CookieSettings = ({ open, onOpenChange, currentStatus }: CookieSettingsProps) => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(currentStatus === 'accepted');

  useEffect(() => {
    setAnalyticsEnabled(currentStatus === 'accepted');
  }, [currentStatus]);

  // Handle escape key
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleSave = () => {
    const newStatus = analyticsEnabled ? 'accepted' : 'rejected';
    setConsent(newStatus);
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Cookie settings"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={() => onOpenChange(false)}
      />

      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-lg animate-in zoom-in-95 fade-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-medium text-foreground">Cookie settings</h2>
          <button
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1"
            aria-label="Close settings"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          <p className="text-sm text-muted-foreground">
            Manage your cookie preferences. Essential cookies cannot be disabled as they are necessary for the site to function.
          </p>

          {/* Essential Cookies */}
          <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex-shrink-0 mt-0.5">
              <Cookie className="h-5 w-5 text-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-medium text-foreground">Essential cookies</h3>
                <Switch checked={true} disabled aria-label="Essential cookies (always enabled)" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Required for the site to function. Includes consent preferences and basic functionality.
              </p>
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex-shrink-0 mt-0.5">
              <BarChart3 className="h-5 w-5 text-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-medium text-foreground">Analytics</h3>
                <Switch
                  checked={analyticsEnabled}
                  onCheckedChange={setAnalyticsEnabled}
                  aria-label="Analytics cookies"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Helps us understand how visitors use the site. Data is anonymized and never sold.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            className="bg-foreground text-background hover:bg-foreground/90"
          >
            Save preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieSettings;
